"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Apple, Play, GripVertical } from "lucide-react";
export default function TheFutureOfItSupport() {
  const [position, setPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = x / rect.width * 100;
    setPosition(percent);
  }, []);
  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  }, [isDragging, handleMove]);
  const onTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  }, [isDragging, handleMove]);
  const onMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
      window.addEventListener("touchmove", onTouchMove, {
        passive: false
      });
      window.addEventListener("touchend", onMouseUp);
    } else {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onMouseUp);
    };
  }, [isDragging, onMouseMove, onTouchMove, onMouseUp]);
  return <section data-section-id="230333" id="the-future-of-it-support" className="bg-background text-foreground relative w-full h-[85vh] min-h-[600px] overflow-hidden select-none">
      <div ref={containerRef} className="relative w-full h-full cursor-ew-resize" onMouseDown={e => {
      setIsDragging(true);
      handleMove(e.clientX);
    }} onTouchStart={e => {
      setIsDragging(true);
      handleMove(e.touches[0].clientX);
    }}>
        {}
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686883/img/ai_core_visualization_active.png" alt="Autonomous Intelligence Core Active" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />

        {}
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686883/img/legacy_it_infrastructure_static.png" alt="Legacy IT Infrastructure" className="absolute inset-0 w-full h-full object-cover pointer-events-none" style={{
        clipPath: `inset(0 ${100 - position}% 0 0)`
      }} />

        {}
        <div className="absolute inset-y-0 w-0.5 bg-primary/70 transform -translate-x-1/2 flex items-center justify-center pointer-events-none z-20" style={{
        left: `${position}%`
      }}>
          <div className="w-12 h-12 bg-secondary/40 backdrop-blur-md border border-primary/30 rounded-lg flex items-center justify-center shadow-xl text-primary pointer-events-auto cursor-grab active:cursor-grabbing transition-transform hover:scale-110">
            <GripVertical className="size-6" />
          </div>
        </div>

        {}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10 p-6 bg-radial-gradient from-primary/5 to-transparent">
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold font-default text-foreground text-center tracking-tighter drop-shadow-[0_0_30px_rgba(0,255,255,0.3)]">
            <span className="bg-gradient-to-r from-primary to-foreground bg-clip-text text-transparent">Autonomous Intelligence</span> for the Modern Enterprise
          </h2>
          <p className="mt-6 max-w-2xl text-center text-muted-foreground font-default text-lg md:text-xl drop-shadow-md">
            Deploy AI support agents that solve complex cases instantly, reducing operational costs while maximizing technical efficiency.
          </p>
        </div>

        {}
        <div className="absolute bottom-12 left-0 right-0 flex flex-col sm:flex-row items-center justify-center gap-4 z-30 px-6 pointer-events-none">
          <Button asChild variant="default" size="lg" className="pointer-events-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg h-14 px-8 text-base font-bold font-default transition-all duration-300">
            <Link to="/SupportHubPage">
              Deploy Now
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="pointer-events-auto bg-secondary/20 border-primary/30 text-foreground hover:bg-secondary/40 backdrop-blur-md rounded-lg h-14 px-8 text-base font-medium font-mono shadow-lg transition-all duration-300">
            <Link to="/ServiceTiersPage">
              View Documentation
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}